import React, { useState, useRef, useEffect } from "react";

interface Props {
  onCancel: () => void;
  onCreateEngagement: (
    companyName: string,
    taxRef: string,
    functionalCurrency: string,
    yearOfAssessment: string,
    basisPeriodStart: string,
    basisPeriodEnd: string
  ) => void;
}

const CURRENCIES = [
  "SGD", // Singapore Dollar
  "AUD", // Australian Dollar
  "USD", // US Dollar
  "EUR", // Euro
  "GBP", // British Pound Sterling
  "JPY", // Japanese Yen
  "CNY", // Chinese Yuan (Renminbi)
  "CHF", // Swiss Franc
  "CAD", // Canadian Dollar
  "NZD", // New Zealand Dollar
  "HKD", // Hong Kong Dollar
  "KRW", // South Korean Won
  "INR", // Indian Rupee
  "IDR", // Indonesian Rupiah
  "MYR", // Malaysian Ringgit
  "THB", // Thai Baht
  "PHP", // Philippine Peso
  "VND", // Vietnamese Dong
];

export default function NewClientEngagementModal({ onCancel, onCreateEngagement }: Props) {
  const [companyName, setCompanyName] = useState("");
  const [taxRef, setTaxRef] = useState("");
  const [functionalCurrency, setFunctionalCurrency] = useState("SGD");
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const currencyRef = useRef<HTMLDivElement>(null);
  const [yearOfAssessment, setYearOfAssessment] = useState("2026");
  const [directorsNames, setDirectorsNames] = useState<string[]>([""]);
  const [errors, setErrors] = useState<{ companyName?: string; taxRef?: string }>({});

  // Basis period defaults: 1 Jan – 31 Dec of (YA - 1)
  const [basisPeriodStart, setBasisPeriodStart] = useState("2025-01-01");
  const [basisPeriodEnd, setBasisPeriodEnd] = useState("2025-12-31");

  // Close currency dropdown when clicking outside
  useEffect(() => {
    function handleOutsideClick(e: MouseEvent) {
      if (currencyRef.current && !currencyRef.current.contains(e.target as Node)) {
        setCurrencyOpen(false);
      }
    }
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const validate = () => {
    const next: typeof errors = {};
    if (!companyName.trim()) next.companyName = "Company name is required";
    if (!taxRef.trim()) next.taxRef = "Tax reference is required";
    return next;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    onCreateEngagement(companyName.trim(), taxRef.trim(), functionalCurrency.trim() || "SGD", yearOfAssessment, basisPeriodStart, basisPeriodEnd);
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "11px 14px",
    borderRadius: 9,
    border: "1px solid var(--border)",
    background: "var(--card-bg)",
    color: "var(--color)",
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.2s ease",
  };

  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: 12,
    fontWeight: 600,
    color: "#94a3b8",
    marginBottom: 6,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  };

  const errorStyle: React.CSSProperties = {
    fontSize: 12,
    color: "#ef4444",
    marginTop: 4,
  };

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        background: "var(--bg-color)",
        borderRadius: 18,
        width: 720,
        maxWidth: "92vw",
        maxHeight: "92vh",
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 30px 70px rgba(0,0,0,0.45)",
        overflow: "hidden",
        fontFamily: '"Inter", "Roboto", "Helvetica Neue", sans-serif',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "24px 28px 20px",
          borderBottom: "1px solid var(--border)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "var(--color)" }}>
            New Client Engagement
          </h2>
        </div>
        <button
          onClick={onCancel}
          style={{
            background: "transparent",
            border: "none",
            color: "var(--color)",
            fontSize: 26,
            cursor: "pointer",
            lineHeight: 1,
            opacity: 0.6,
          }}
        >
          ×
        </button>
      </div>

      {/* Body */}
      <form
        onSubmit={handleSubmit}
        style={{
          padding: "24px 28px 28px",
          overflowY: "auto",
          flex: 1,
          minHeight: 0,
        }}
      >
        <p style={{ margin: "0 0 20px", fontSize: 13, color: "#64748b", lineHeight: 1.6 }}>
          Enter the client details to commence a new client engagement.
        </p>

        {/* Company Name */}
        <div style={{ marginBottom: 18 }}>
          <label style={labelStyle}>Company Name</label>
          <input
            type="text"
            value={companyName}
            onChange={(e) => {
              setCompanyName(e.target.value);
              if (errors.companyName) setErrors((p) => ({ ...p, companyName: undefined }));
            }}
            placeholder="e.g. Acme Pte Ltd"
            style={{
              ...inputStyle,
              borderColor: errors.companyName ? "#ef4444" : undefined,
            }}
            autoFocus
          />
          {errors.companyName && <div style={errorStyle}>{errors.companyName}</div>}
        </div>

        {/* Tax Reference */}
        <div style={{ marginBottom: 18 }}>
          <label style={labelStyle}>Tax Reference No.</label>
          <input
            type="text"
            value={taxRef}
            onChange={(e) => {
              setTaxRef(e.target.value);
              if (errors.taxRef) setErrors((p) => ({ ...p, taxRef: undefined }));
            }}
            placeholder="e.g. 200012345A"
            style={{
              ...inputStyle,
              borderColor: errors.taxRef ? "#ef4444" : undefined,
            }}
          />
          {errors.taxRef && <div style={errorStyle}>{errors.taxRef}</div>}
        </div>

        {/* Functional Currency — custom combobox */}
        <div style={{ marginBottom: 18 }} ref={currencyRef}>
          <label style={labelStyle}>Functional Currency</label>
          <div style={{ position: "relative" }}>
            {/* Text input */}
            <input
              id="functional-currency-input"
              type="text"
              value={functionalCurrency}
              onChange={(e) => {
                setFunctionalCurrency(e.target.value.toUpperCase());
                setCurrencyOpen(true);
              }}
              onClick={() => setCurrencyOpen((prev) => !prev)}
              placeholder="e.g. SGD"
              autoComplete="off"
              style={{
                ...inputStyle,
                paddingRight: 40,
                cursor: "pointer",
              }}
            />
            {/* Chevron toggle button */}
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setCurrencyOpen((prev) => !prev)}
              style={{
                position: "absolute",
                right: 0,
                top: 0,
                bottom: 0,
                width: 38,
                background: "transparent",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#94a3b8",
                fontSize: 16,
                transition: "transform 0.2s ease",
                transform: currencyOpen ? "rotate(180deg)" : "rotate(0deg)",
              }}
            >
              ▾
            </button>

            {/* Dropdown list */}
            {currencyOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 4px)",
                  left: 0,
                  right: 0,
                  background: "var(--card-bg, #1e2330)",
                  border: "1px solid var(--border)",
                  borderRadius: 9,
                  boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
                  zIndex: 9999,
                  maxHeight: 220,
                  overflowY: "auto",
                  padding: "6px 0",
                }}
              >
                {CURRENCIES.filter((c) =>
                  c.startsWith(functionalCurrency.toUpperCase()) ||
                  functionalCurrency === ""
                ).map((code) => (
                  <div
                    key={code}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setFunctionalCurrency(code);
                      setCurrencyOpen(false);
                    }}
                    style={{
                      padding: "9px 14px",
                      fontSize: 14,
                      fontWeight: functionalCurrency === code ? 700 : 400,
                      color: functionalCurrency === code ? "#3b82f6" : "var(--color)",
                      background: functionalCurrency === code
                        ? "rgba(59,130,246,0.08)"
                        : "transparent",
                      cursor: "pointer",
                      borderRadius: 6,
                      margin: "0 4px",
                      transition: "background 0.12s ease",
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLDivElement).style.background = "rgba(59,130,246,0.12)";
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLDivElement).style.background =
                        functionalCurrency === code ? "rgba(59,130,246,0.08)" : "transparent";
                    }}
                  >
                    {code}
                  </div>
                ))}
                {/* No match — show typed value as option */}
                {CURRENCIES.filter((c) => c.startsWith(functionalCurrency.toUpperCase())).length === 0 &&
                  functionalCurrency.trim() !== "" && (
                  <div
                    style={{
                      padding: "9px 14px",
                      fontSize: 13,
                      color: "#64748b",
                      fontStyle: "italic",
                    }}
                  >
                    Using custom: {functionalCurrency.toUpperCase()}
                  </div>
                )}
              </div>
            )}
          </div>
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 5 }}>
            Select from the list or type a custom currency code.
          </div>
        </div>

        {/* Directors' Names */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <label style={labelStyle}>Directors' Names</label>
            {directorsNames.length < 4 && (
              <button
                type="button"
                onClick={() => setDirectorsNames((prev) => [...prev, ""])}
                style={{
                  background: "transparent",
                  border: "1px solid var(--border)",
                  borderRadius: 6,
                  color: "#3b82f6",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  padding: "3px 10px",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  transition: "all 0.15s ease",
                }}
              >
                + Add Director
              </button>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {directorsNames.map((name, i) => (
              <div key={i} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => {
                    const updated = [...directorsNames];
                    updated[i] = e.target.value;
                    setDirectorsNames(updated);
                  }}
                  placeholder={`Director ${i + 1} full name`}
                  style={inputStyle}
                />
                {directorsNames.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setDirectorsNames((prev) => prev.filter((_, idx) => idx !== i))}
                    title="Remove"
                    style={{
                      flexShrink: 0,
                      width: 32,
                      height: 32,
                      borderRadius: 6,
                      background: "rgba(239,68,68,0.08)",
                      border: "1px solid rgba(239,68,68,0.3)",
                      color: "#ef4444",
                      cursor: "pointer",
                      fontSize: 18,
                      lineHeight: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.15s ease",
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 6 }}>
            Up to 4 directors. At least 1 recommended.
          </div>
        </div>

        {/* Basis Period */}
        <div style={{ marginBottom: 24 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr auto 1fr",
              gap: 12,
              alignItems: "end",
            }}
          >
            {/* Start of Basis Period */}
            <div>
              <label style={labelStyle}>Start of Basis Period</label>
              <div style={{ position: "relative" }}>
                <input
                  type="date"
                  value={basisPeriodStart}
                  onChange={(e) => setBasisPeriodStart(e.target.value)}
                  style={{
                    ...inputStyle,
                    paddingRight: 14,
                    colorScheme: "dark",
                  }}
                />
              </div>
            </div>

            {/* Dash separator */}
            <div
              style={{
                paddingBottom: 12,
                color: "#94a3b8",
                fontSize: 18,
                fontWeight: 300,
                textAlign: "center",
                userSelect: "none",
              }}
            >
              –
            </div>

            {/* End of Basis Period */}
            <div>
              <label style={labelStyle}>End of Basis Period</label>
              <div style={{ position: "relative" }}>
                <input
                  type="date"
                  value={basisPeriodEnd}
                  onChange={(e) => setBasisPeriodEnd(e.target.value)}
                  style={{
                    ...inputStyle,
                    paddingRight: 14,
                    colorScheme: "dark",
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Important Checklist */}
        <div
          style={{
            border: "2px solid #ef4444",
            borderRadius: 10,
            padding: "18px 20px",
            marginBottom: 28,
            background: "rgba(239, 68, 68, 0.03)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 16,
            }}
          >
            <div
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: "#ef4444",
              }}
            />
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#ef4444",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
              }}
            >
              Important — Action Required Before Proceeding
            </div>
          </div>

          {/* Checklist Items */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {[
              {
                num: "01",
                title: "Reconcile NPBT / NPAT to financial statements",
                desc: "Net profit before tax per the detailed profit and loss accounts ",
              },
              {
                num: "02",
                title: "Reconcile Fixed Assets movement to financial statements",
                desc: "Additions and / or disposals to fixed assets per the Fixed Asset Register",
              },
            ].map((item) => (
              <div
                key={item.num}
                style={{
                  display: "flex",
                  gap: 12,
                  padding: "12px 14px",
                  background: "var(--bg-color)",
                  borderRadius: 8,
                  border: "1px solid var(--border)",
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 6,
                    background: "rgba(239, 68, 68, 0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#ef4444",
                    flexShrink: 0,
                  }}
                >
                  {item.num}
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: "var(--color)",
                      marginBottom: 4,
                      lineHeight: 1.4,
                    }}
                  >
                    {item.title}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "#64748b",
                      lineHeight: 1.5,
                    }}
                  >
                    {item.desc}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: "10px 22px",
              borderRadius: 8,
              background: "transparent",
              border: "1px solid var(--border)",
              color: "var(--color)",
              fontWeight: 600,
              cursor: "pointer",
              fontSize: 14,
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            style={{
              padding: "10px 28px",
              borderRadius: 8,
              background: "linear-gradient(90deg, #3b82f6, #a855f7)",
              color: "#fff",
              fontWeight: 700,
              cursor: "pointer",
              fontSize: 14,
              border: "none",
              boxShadow: "0 4px 14px rgba(59,130,246,0.35)",
            }}
          >
            Next — Upload Documents →
          </button>
        </div>
      </form>
    </div>
  );
}
